'use client';

import React, { useState } from 'react';
import { Plus, Search, X, ChevronDown, ChevronUp } from 'lucide-react';
import { MOCK_HEALTH_RECORDS, HEALTH_STATUS_CONFIG, FLOCKS_LIST,  } from './healthData';

function StatusPill({ status }: { status: string }) {
  const cfg = HEALTH_STATUS_CONFIG[status] ?? { label: status, color: '#6b7280', bg: '#f9fafb' };
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold" style={{ color: cfg.color, backgroundColor: cfg.bg }}>
      {cfg.label}
    </span>
  );
}

function AddHealthRecordModal({ onClose }: { onClose: () => void }) {
  const [form, setForm] = useState({
    flock: '', symptoms: '', suspectedCondition: '', diagnosis: '',
    veterinarian: '', treatment: '', medication: '', dosage: '',
    duration: '', birdsAffected: '', cost: '', notes: '',
  });

  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-bold text-gray-900">New Health Record</h2>
            <p className="text-xs text-gray-500 mt-0.5">Record a health incident or disease observation</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100 transition-colors">
            <X size={18} className="text-gray-500" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-xs font-semibold text-gray-700 mb-1">Flock *</label>
              <select required value={form.flock} onChange={e => set('flock', e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500">
                <option value="">Select flock...</option>
                {FLOCKS_LIST.map(f => (
                  <option key={f.id} value={f.id}>{f.number} — {f.name} ({f.house})</option>
                ))}
              </select>
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-semibold text-gray-700 mb-1">Symptoms Observed *</label>
              <input required value={form.symptoms} onChange={e => set('symptoms', e.target.value)}
                placeholder="e.g. Sneezing, nasal discharge, reduced feed intake"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Suspected Condition</label>
              <input value={form.suspectedCondition} onChange={e => set('suspectedCondition', e.target.value)}
                placeholder="e.g. Infectious Bronchitis"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Confirmed Diagnosis</label>
              <input value={form.diagnosis} onChange={e => set('diagnosis', e.target.value)}
                placeholder="e.g. IB confirmed by lab"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Veterinarian</label>
              <input value={form.veterinarian} onChange={e => set('veterinarian', e.target.value)}
                placeholder="Dr. Name"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Birds Affected</label>
              <input type="number" min="0" value={form.birdsAffected} onChange={e => set('birdsAffected', e.target.value)}
                placeholder="0"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-semibold text-gray-700 mb-1">Treatment Plan *</label>
              <textarea required value={form.treatment} onChange={e => set('treatment', e.target.value)}
                rows={2} placeholder="Describe the treatment plan..."
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 resize-none" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Medication Used</label>
              <input value={form.medication} onChange={e => set('medication', e.target.value)}
                placeholder="Medicine name"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Dosage</label>
              <input value={form.dosage} onChange={e => set('dosage', e.target.value)}
                placeholder="e.g. 1ml per litre"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Duration</label>
              <input value={form.duration} onChange={e => set('duration', e.target.value)}
                placeholder="e.g. 5 days"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Estimated Cost (UGX)</label>
              <input type="number" min="0" value={form.cost} onChange={e => set('cost', e.target.value)}
                placeholder="0"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-semibold text-gray-700 mb-1">Notes / Observations</label>
              <textarea value={form.notes} onChange={e => set('notes', e.target.value)}
                rows={2} placeholder="Additional notes..."
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 resize-none" />
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">
              Cancel
            </button>
            <button type="submit"
              className="flex-1 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-colors"
              style={{ backgroundColor: 'var(--primary)' }}>
              Save Health Record
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function HealthRecordsTab() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const filtered = MOCK_HEALTH_RECORDS.filter(r => {
    const matchSearch = search === '' ||
      r.flock.toLowerCase().includes(search.toLowerCase()) ||
      r.recordNumber.toLowerCase().includes(search.toLowerCase()) ||
      r.diagnosis.toLowerCase().includes(search.toLowerCase()) ||
      r.house.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'all' || r.status === statusFilter;
    return matchSearch && matchStatus;
  });

  return (
    <div>
      {showModal && <AddHealthRecordModal onClose={() => setShowModal(false)} />}

      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by flock, diagnosis, record number..."
            className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-green-500" />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500">
          <option value="all">All Statuses</option>
          <option value="open">Open</option>
          <option value="under-treatment">Under Treatment</option>
          <option value="resolved">Resolved</option>
          <option value="referred">Referred</option>
        </select>
        <button onClick={() => setShowModal(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-colors"
          style={{ backgroundColor: 'var(--primary)' }}>
          <Plus size={15} /> New Record
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100" style={{ backgroundColor: '#f8fafc' }}>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Record #</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Date</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Flock / House</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Diagnosis</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Veterinarian</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Cost (UGX)</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center py-12 text-gray-400 text-sm">No health records found.</td>
                </tr>
              )}
              {filtered.map(record => (
                <React.Fragment key={record.id}>
                  <tr className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 font-mono text-xs font-semibold text-gray-700">{record.recordNumber}</td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">{record.date}</td>
                    <td className="px-4 py-3">
                      <div className="font-semibold text-gray-800 text-xs">{record.flock}</div>
                      <div className="text-xs text-gray-400">{record.house} · {record.farm}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-800 text-xs">{record.diagnosis}</div>
                      <div className="text-xs text-gray-400">{record.birdsAffected} birds affected</div>
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-600">{record.veterinarian}</td>
                    <td className="px-4 py-3 text-right font-semibold text-gray-800 text-xs">
                      {record.cost.toLocaleString()}
                    </td>
                    <td className="px-4 py-3"><StatusPill status={record.status} /></td>
                    <td className="px-4 py-3">
                      <button onClick={() => setExpandedId(expandedId === record.id ? null : record.id)}
                        className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors text-gray-400">
                        {expandedId === record.id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                      </button>
                    </td>
                  </tr>
                  {expandedId === record.id && (
                    <tr className="bg-blue-50/30">
                      <td colSpan={8} className="px-6 py-4">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
                          <div>
                            <div className="font-semibold text-gray-700 mb-1">Symptoms</div>
                            <div className="flex flex-wrap gap-1">
                              {record.symptoms.map(s => (
                                <span key={s} className="px-2 py-0.5 bg-red-50 text-red-700 rounded-full text-xs">{s}</span>
                              ))}
                            </div>
                          </div>
                          <div>
                            <div className="font-semibold text-gray-700 mb-1">Treatment</div>
                            <div className="text-gray-600">{record.treatment}</div>
                            {record.medication && (
                              <div className="mt-1 text-gray-500">
                                <span className="font-medium">Med:</span> {record.medication} · {record.dosage} · {record.duration}
                              </div>
                            )}
                          </div>
                          <div>
                            <div className="font-semibold text-gray-700 mb-1">Notes</div>
                            <div className="text-gray-600">{record.notes}</div>
                            <div className="mt-1 text-gray-400">Recorded by: {record.createdBy}</div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between">
          <span className="text-xs text-gray-400">{filtered.length} record{filtered.length !== 1 ? 's' : ''}</span>
        </div>
      </div>
    </div>
  );
}
